import React, { useMemo, useState } from 'react';
import RevenueView from './RevenueView';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useQuery } from 'react-query';
import { removeNulls } from '../deal-commissions/utils';
import { endOfDay, startOfMonth, startOfYear } from 'date-fns';

const FIVE_MINUTES = 5 * 60 * 1000;
const FIFTEEN_MINUTES = 15 * 60 * 1000;
const TARGET = 500_000; // TODO: Allow configuring this

const RevenueViewContainer = () => {
  const axios = useAxiosPrivate();

  const [includeEoi, setIncludeEoi] = useState(true);

  const thisMonthParams = useMemo(
    () => {
      const today = new Date();
      const startOfThisMonth = startOfMonth(today).toISOString();
      const endOfToday = endOfDay(today).toISOString();
      
      return {
        fromDate: startOfThisMonth,
        toDate: endOfToday,
        transactions: {
          fromPaymentDate: startOfThisMonth,
          toPaymentDate: endOfToday
        }
      };
    },
    []
  );

  const thisYearParams = useMemo(
    () => {
      const today = new Date();
      const startOfThisYear = startOfYear(today).toISOString();
      const endOfToday = endOfDay(today).toISOString();
      
      return {
        fromDate: startOfThisYear,
        toDate: endOfToday,
        transactions: {
          fromPaymentDate: startOfThisYear,
          toPaymentDate: endOfToday
        }
      };
    },
    []
  );

  // Gross earnings query
  const { data: earnings, isError: earningsIsError, isLoading: earningsLoading } = useQuery(
    ['finances'],
    async () => {
      const res = await axios.get('/deals/finances');
      return res?.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: FIVE_MINUTES,
      keepPreviousData: true
    }
  );

  const { data: earningsThisYear, isError: earningsThisYearIsError, isLoading: earningsThisYearLoading } = useQuery(
    ['finances-this-year', thisYearParams],
    async () => {
      const res = await axios.get('/deals/finances', {
        params: removeNulls(thisYearParams)
      });
      return res?.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: FIVE_MINUTES,
      keepPreviousData: true
    }
  );

  const { data: netReceivedThisMonth, isError: netReceivedThisMonthIsError, isLoading: netReceivedThisMonthLoading } = useQuery(
    ['net-received-this-month', thisMonthParams],
    async () => {
      const res = await axios.get('/deals/received', {
        params: removeNulls(thisMonthParams)
      });
      return res?.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: FIVE_MINUTES,
      keepPreviousData: true
    }
  );

  const { data: netReceivedThisYear, isError: netReceivedThisYearIsError, isLoading: netReceivedThisYearLoading } = useQuery(
    ['net-received-this-year', thisYearParams],
    async () => {
      const res = await axios.get('/deals/received', {
        params: removeNulls(thisYearParams)
      });
      return res?.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: FIVE_MINUTES,
      keepPreviousData: true
    }
  );

  // Comparison data queries
  const { data: netReceivedYearlyComparison, isError: netReceivedYearlyComparisonIsError, isLoading: netReceivedYearlyComparisonLoading } = useQuery(
    ['net-received-yearly-comparison'],
    async () => {
      const res = await axios.get('/deals/received/yearly-comparison');
      return res?.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: FIVE_MINUTES,
      keepPreviousData: true
    }
  );
  
  const { data: netReceivedMonthlyComparison, isError: netReceivedMonthlyComparisonIsError, isLoading: netReceivedMonthlyComparisonLoading } = useQuery(
    ['net-received-monthly-comparison'],
    async () => {
      const res = await axios.get('/deals/received/monthly-comparison');
      return res?.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: FIVE_MINUTES,
      keepPreviousData: true
    }
  );

  const { data: netReceivedTargetComparison, isError: netReceivedTargetComparisonIsError, isLoading: netReceivedTargetComparisonLoading } = useQuery(
    ['net-received-target-comparison'],
    async () => {
      const res = await axios.get(`/deals/received/target-comparison?target=${TARGET}`);
      return res?.data;
    },
    {
      refetchOnWindowFocus: true,
      refetchInterval: FIVE_MINUTES,
      keepPreviousData: true
    }
  );

  const { data: dealProjections, isError: dealProjectionsIsError, isLoading: dealProjectionsLoading, refetch: dealProjectionsRefetch } = useQuery(
    ['deal-projections', { includeEoi }],
    async () => {
      const res = await axios.get('/deals/projections', {
        params: {
          includeEOI: includeEoi ? '1' : '0',
        }
      });
      console.log(res?.data, "API RESULT");
      return res?.data?.data ?? [];
    },
    {
      staleTime: FIFTEEN_MINUTES,
      refetchOnWindowFocus: false,
      refetchInterval: FIFTEEN_MINUTES,
    }
  )  

  return (
    <div style={{ marginTop: '80px' }}>
      <RevenueView
        data={{
          earnings: { data: earnings, isError: earningsIsError, isLoading: earningsLoading },
          earningsThisYear: { data: earningsThisYear, isError: earningsThisYearIsError, isLoading: earningsThisYearLoading },
          netReceivedThisMonth: { data: netReceivedThisMonth, isError: netReceivedThisMonthIsError, isLoading: netReceivedThisMonthLoading },
          netReceivedThisYear: { data: netReceivedThisYear, isError: netReceivedThisYearIsError, isLoading: netReceivedThisYearLoading },
          netReceivedYearlyComparison: { data: netReceivedYearlyComparison, isError: netReceivedYearlyComparisonIsError, isLoading: netReceivedYearlyComparisonLoading },
          netReceivedMonthlyComparison: { data: netReceivedMonthlyComparison, isError: netReceivedMonthlyComparisonIsError, isLoading: netReceivedMonthlyComparisonLoading },
          netReceivedTargetComparison: { data: netReceivedTargetComparison, isError: netReceivedTargetComparisonIsError, isLoading: netReceivedTargetComparisonLoading },
          dealProjections: { data: dealProjections, isError: dealProjectionsIsError, isLoading: dealProjectionsLoading, refetch: dealProjectionsRefetch }
        }}
        includeEoi={includeEoi}
        setIncludeEoi={setIncludeEoi}
      />
    </div>
  );
};

export default RevenueViewContainer;
